import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { REVIEWS, REVIEW_COUNT, AVG_RATING } from "@/data/reviews";
import { TldrBox } from "@/components/tldr-box";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import heroImg from "@/assets/dj-mariage-dance.jpg";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";
import { getPublicReviews } from "@/lib/site-reviews.functions";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";

const EVENT_TYPES = ["Tous", "Mariage", "Anniversaire", "Entreprise", "Cérémonie", "Soirée privée"] as const;

export const Route = createFileRoute("/avis-clients")({
  /**
   * On lit les avis reellement affiches sur la page (source Google via Featurable,
   * repli base de donnees) pour n'exposer qu'une note moyenne verifiable.
   */
  loader: async () => {
    try {
      const r = await getPublicReviews();
      const ratings = r.rows.map((x) => x.rating).filter((n) => typeof n === "number" && n > 0);
      if (!ratings.length || !r.total) return { rating: null as number | null, count: 0 };
      const average = r.average ?? ratings.reduce((a, b) => a + b, 0) / ratings.length;
      return { rating: Math.round(average * 10) / 10, count: r.total };
    } catch {
      return { rating: null as number | null, count: 0 };
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "Avis DJ mariage Vaucluse : 5/5 Google | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Témoignages de mariés, particuliers et entreprises ayant fait confiance à DJ Val dans le Vaucluse et en Provence. Note moyenne de 5 sur 5 sur Google.")
      },
      { property: "og:title", content: "Avis DJ Mariage Vaucluse — 5/5 Google | DJ Val" },
      { property: "og:description", content: metaDesc("18 témoignages vérifiés, note moyenne 5/5, lieux et types d'événements détaillés.") },
      { property: "og:url", content: "https://dj-val.fr/avis-clients" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/avis-clients" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Avis clients DJ Val",
          description:
            "Témoignages de mariés, particuliers et entreprises ayant fait appel à DJ Val en Provence.",
          url: "https://dj-val.fr/avis-clients",
          isPartOf: { "@id": "https://dj-val.fr/#website" },
          about: { "@id": "https://dj-val.fr/#business" },
        }),
      },
      // AggregateRating : uniquement si la moyenne reelle des avis affiches est calculable.
      ...(loaderData?.rating && loaderData.count
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                ...LOCAL_BUSINESS_SCHEMA,
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: String(loaderData.rating),
                  reviewCount: String(loaderData.count),
                  bestRating: "5",
                  worstRating: "1",
                },
              }),
            },
          ]
        : []),
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  const [filter, setFilter] = useState<(typeof EVENT_TYPES)[number]>("Tous");
  const filtered = filter === "Tous" ? REVIEWS : REVIEWS.filter((r) => r.eventType === filter);

  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Témoignages vérifiés"
        title={<>La voix de <span className="italic">nos mariés</span></>}
        subtitle={<>★★★★★ &nbsp;{AVG_RATING.toFixed(1)}/5 · {REVIEW_COUNT} témoignages détaillés en plus des 41 avis Google.</>}
        primary={{ label: "Demander un devis", to: "/contactez-nous" }}
        secondary={{ label: "Voir mes réalisations", to: "/realisations" }}
        height="compact"
      />


      <div className="px-6">
        <TldrBox
          title="En bref"
          summary="DJ Val cumule 5/5 sur 41 avis Google et 18 témoignages détaillés de mariés, particuliers et entreprises en Provence. Points forts cités : lecture de salle, transparence tarifaire, matériel pro Pioneer/RCF/Shure et accompagnement de bout en bout."
          items={[
            { label: "Note moyenne", value: `${AVG_RATING.toFixed(1)}/5` },
            { label: "Témoignages", value: `${REVIEW_COUNT} détaillés + 41 Google` },
            { label: "Zone", value: "Vaucluse, Bouches-du-Rhône, Gard" },
            { label: "Types d'événements", value: "Mariages · Entreprise · Anniversaires" },
          ]}
        />
      </div>

      <section className="py-8 px-6 sticky top-16 lg:top-20 z-30 bg-background/95 backdrop-blur border-b border-foreground/10">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
          {EVENT_TYPES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                filter === c
                  ? "border-[var(--gold)] text-foreground bg-[var(--gold)]/10"
                  : "border-foreground/10 text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="grid gap-6 sm:grid-cols-2">
          {filtered.map((r) => (
            <article
              key={r.id}
              className="reveal border border-foreground/10 bg-card/40 p-6 sm:p-8 flex flex-col gap-4 hover:border-[var(--gold)]/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-[var(--gold)] tracking-[0.3em]">{"★".repeat(r.rating)}</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  {new Date(r.date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                </span>
              </div>
              <p className="text-sm text-foreground/85 font-light leading-relaxed italic">
                « {r.text} »
              </p>
              <div className="pt-3 border-t border-foreground/10 text-xs">
                <p className="text-foreground font-medium">{r.author}</p>
                <p className="text-muted-foreground mt-0.5">
                  {r.eventType} · {r.city}
                  {r.venue ? ` · ${r.venue}` : ""}
                  {r.guests ? ` · ${r.guests} invités` : ""}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 bg-card border-y border-[var(--gold-line)] text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">
            Votre événement sera le prochain témoignage
          </h2>
          <span className="gold-rule mx-auto !w-12" />
          <p className="text-sm font-light">
            Demandez votre devis personnalisé sous 24 h, sans engagement.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Link
              to="/contactez-nous"
              className="inline-block px-8 py-3 border border-[var(--gold)] text-foreground text-xs uppercase tracking-[0.3em] hover:bg-[var(--gold)]/10"
            >
              Demander un devis
            </Link>
            <a
              href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "avis_clients" })}
              className="inline-block px-8 py-3 bg-[var(--gold)] text-[var(--ink)] text-xs uppercase tracking-[0.3em]"
            >
              07 67 90 86 67
            </a>
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJn2-Ifaz3tRIRQGJB4UPeKaw" onClick={() => trackEvent("outbound_click", { link_url: "https://search.google.com/local/writereview?placeid=ChIJn2-Ifaz3tRIRQGJB4UPeKaw", cta_location: "avis_clients_write" })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs uppercase tracking-[0.3em] hover:border-[var(--gold)]"
            >
              Laisser un avis Google
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
